<template>
  <div class="sb_toolbar">
    <div class="sb_toolbar_container" :class="{ s_active: toolbar }">
      <div class="sb_toolbar_box">
        <Wrapper class="sb_toolbar_wrapper">
          <Dimension :mode="design.type" class="sb_toolbar_dimensions" @canvasUpdate="calculateDPI" />
          <transition name="fade">
            <div class="sb_toolbar_dpi" v-if="dpiView">
              <button
                class="sb_toolbar_dpi_button"
                :class="dpiVariant"
                @mouseleave="stopInfoActive"
                @click="dpiVariant === 's_danger' ? (infoActive = !infoActive) : null"
              >
                DPI: {{ dpiView > 150 ? 'OK' : dpiView }}
                <transition name="fade">
                  <SvgIcon v-if="dpiVariant === 's_danger'" class="sb_toolbar_dpi_button_info" icon="info-alt" />
                </transition>
              </button>
              <transition name="fade">
                <span v-if="dpiVariant === 's_danger'" class="sb_toolbar_dpi_info" :class="{ s_active: infoActive }">
                  <SvgIcon class="sb_toolbar_dpi_info_icon" icon="info" />
                  <span>
                    Oeps! De DPI meter kleurt rood wat betekent dat de kwaliteit niet goed zal zijn. Probeer je
                    printformaat te verkleinen voor een beter resultaat.<br /><br />Kijk voor meer uitleg op
                    <a href="https://customfotowand.nl/beeldkwaliteit-fotobehang/" target="_blank">beeldkwaliteit</a>.
                  </span>
                </span>
                <span
                  v-else-if="dpiVariant === 's_warning'"
                  class="sb_toolbar_dpi_info"
                  :class="{ s_active: infoActive }"
                >
                  <SvgIcon class="sb_toolbar_dpi_info_icon" icon="info" />
                  <span>
                    De resolutie van jouw {{ dpiType }} is hoog genoeg voor veraf, maar te laag voor een mooi effect van
                    dichtbij.
                  </span>
                </span>
              </transition>
            </div>
          </transition>
          <SwitchMode />
        </Wrapper>
        <button @click="$emit('update:toolbar', !toolbar)" class="sb_toolbar_show">
          <Wrapper class="sb_toolbar_show_wrapper">
            <span>{{ !toolbar ? 'Bekijk instellingen' : 'Verberg instellingen' }}</span>
            <SvgIcon class="sb_toolbar_show_icon" :icon="toolbar ? 'caret' : 'caret-alt'" />
          </Wrapper>
        </button>
      </div>
      <transition name="fade">
        <div v-if="toolbar" class="sb_toolbar_overlay" @click="$emit('update:toolbar', false)" />
      </transition>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import Dimension from './Dimension.vue';
import SwitchMode from './SwitchMode.vue';
import { CMtoInches } from '@/lib/units';
import { Design } from '@/types';

export default {
  props: ['toolbar'],
  data: () => ({
    // eslint-disable-next-line
    infoTimeout: null,
    infoActive: null,
    active: false,
    dpiView: null,
    dpiType: null,
  }),
  computed: {
    ...mapState('canvas', ['manager']),
    ...mapState('design', ['design']),
    width() {
      return this.design?.dimensions.width;
    },
    height() {
      return this.design?.dimensions.height;
    },
    dpiVariant() {
      if (this.dpiView < 30) {
        return 's_danger';
      }
      // else if (this.dpiView < 70) {
      //   return 's_warning';
      // }
      return null;
    },
  },
  async mounted() {
    window.addEventListener('object:removed', this.calculateDPI);
    window.addEventListener('object:modified', this.calculateDPI);
    window.addEventListener('object:loaded', this.calculateDPI);
  },
  beforeDestroy() {
    window.removeEventListener('object:removed', this.calculateDPI);
    window.removeEventListener('object:modified', this.calculateDPI);
    window.removeEventListener('object:loaded', this.calculateDPI);
  },
  methods: {
    ...mapMutations('design', ['setDesignDimensions']),
    stopInfoActive() {
      clearTimeout(this.infoTimeout);
      this.infoTimeout = setTimeout(() => {
        this.infoActive = false;
      }, 1000);
    },
    getDpi(detail) {
      let dpi;
      if (detail && detail._originalElement.src.includes('svg+xml')) {
        dpi = 300;
      } else if (detail) {
        let { width, height } = detail;
        const bbox = detail.getBoundingRect();
        const ratio = this.width / this.height;

        const { original, type } = detail.data;
        if (original) {
          width = original.width;
          height = original.height;
        }
        const ratioOfSelection = width / height;

        switch (type) {
          case 'Background':
            if (ratio > ratioOfSelection) {
              height = (this.height * width) / this.width;
            } else {
              width = (this.width * height) / this.height;
            }
            break;
          default:
            width /= detail.scaleX;
            height /= detail.scaleY;
            break;
        }

        dpi = Math.min(Math.round(width / CMtoInches(this.width)), Math.round(height / CMtoInches(this.height)));
      }

      return dpi;
    },
    calculateDPI() {
      // backgroundImage
      const { backgroundImage } = this.manager;
      const bgDpi = this.getDpi(backgroundImage);

      // image
      const { imageObject } = this.manager;
      const imgDpi = this.getDpi(imageObject);

      if (imgDpi && bgDpi > imgDpi) {
        this.dpiType = 'afbeelding';
        this.dpiView = imgDpi;
      } else if (bgDpi) {
        this.dpiType = 'afbeelding';
        this.dpiView = bgDpi;
      }

      this.setDesignDimensions({ dpi: this.dpiView });

      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
  },
  components: {
    Dimension,
    SwitchMode,
  },
};
</script>

<style lang="scss">
#sb_fotowand-designer {
  .sb_toolbar {
    z-index: 1001;
    min-height: 3rem;
    @include media('>=small') {
      z-index: 1;
      margin: 1.4rem 0 0.6rem;
      position: relative;
    }
    @include media('>=medium-portrait') {
      margin: 2rem 0 1.4rem;
      flex-wrap: nowrap;
    }
    @include media('>=medium') {
      margin: 2rem 0;
    }
    &_overlay {
      flex-grow: 1;
      cursor: n-resize;
      @include media('>=small') {
        display: none;
      }
    }
    &_box {
      padding-top: 1.4rem;
      pointer-events: all;
      transition: transform 0.4s;
      transform: translateY(-100%) translateY(3rem) translateY(-3px);
      @include media('>=small') {
        transform: none;
        padding: 0;
      }
    }
    &_container {
      display: flex;
      flex-wrap: wrap;
      position: absolute;
      flex-direction: column;
      height: 100%;
      left: 0;
      top: 0;
      width: 100%;
      transition: transform 0.4s;
      z-index: 1001;
      pointer-events: none;
      &.s_active {
        pointer-events: all;
        .sb_toolbar_box {
          transform: translateY(0);
        }
      }
      @include media('>=small') {
        padding: 0 !important;
        position: static;
        transform: none;
        transition: none;
        top: auto;
        left: auto;
      }
      @include media('>=medium-portrait') {
        padding: 0 1rem !important;
      }
    }
    &_wrapper {
      display: flex;
      flex-wrap: wrap;
    }
    &_show {
      display: flex;
      background: $brand-white;
      align-items: center;
      justify-content: flex-end;
      left: 0;
      top: 100%;
      height: 3rem;
      width: 100%;
      border-bottom: 2px solid $brand-lightest-gray;
      border-top: 2px solid $brand-lightest-gray;
      &:hover {
        cursor: pointer;
      }
      &_wrapper {
        display: flex;
        align-items: center;
      }
      &_icon {
        margin-left: 0.2rem;
        font-size: 1.2rem;
      }
      @include media('>=small') {
        display: none;
      }
    }
    &_dpi {
      font-size: 0.7rem;
      display: flex;
      position: absolute;
      z-index: 1;
      transform: translateY(-2.5rem);
      flex-grow: 1;
      top: 100%;
      align-items: center;
      justify-content: flex-start;
      order: 0;
      height: 2rem;
      transition: background 0.4s;
      &_button {
        background: $brand-primary-light;
        min-width: 5rem;
        height: 100%;
        color: $brand-white;
        border-radius: $default-radius;
        display: flex;
        align-items: center;
        justify-content: center;
        &.s_danger {
          background: $brand-red;
          cursor: pointer;
        }
        &.s_warning {
          background: $brand-secondary;
        }
        &_info {
          color: $brand-red;
          margin-left: 0.4rem;
          cursor: pointer;
          @include media('>=xlarge') {
            display: none !important;
          }
        }
      }

      &_info {
        background: $brand-lighter-gray;
        border-radius: $default-radius;
        padding: 0.2rem;
        display: flex;
        opacity: 0;
        pointer-events: none;
        padding: 0.4rem 0.6rem;
        align-items: center;
        line-height: 1.33;
        margin-left: 1rem;
        font-weight: 700;
        font-family: InterGX, Inter, sans-serif;
        font-size: 0.6rem;
        transition: opacity 0.4s;
        position: absolute;
        left: 100%;
        width: 18rem;
        &.s_active {
          opacity: 1;
          pointer-events: all;
        }
        a {
          color: $brand-primary;
          transition: color 0.4s;
          text-decoration: underline;
          &:hover {
            color: $brand-primary-light;
          }
        }
        &_icon {
          color: $brand-red;
          margin: 0 0.6rem 0 0.2rem;
        }
        @include media('>=xlarge') {
          top: auto;
          opacity: 1;
          left: auto;
          width: auto;
          transform: none;
          position: relative;
          pointer-events: all;
        }
        &:before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          transform: translate(-100%, -50%);
          border-top: 8px solid transparent;
          border-bottom: 8px solid transparent;
          border-right: 8px solid $brand-lighter-gray;
        }
        @include media('>=medium-portrait', '<=xlarge') {
          width: 17rem;
          margin-left: 0;
          transform: translate(-50%, 0.6rem);
          top: 100%;
          position: absolute;
          left: 2.4rem;
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -100%);
            border-left: 8px solid transparent;
            border-bottom: 8px solid $brand-lighter-gray;
            border-right: 8px solid transparent;
          }
        }
      }
      @include media('>=small') {
        transform: translateY(0.5rem);
      }
      @include media('>=medium-portrait') {
        position: relative;
        transform: none;
        order: 0;
        height: 2.5rem;
        top: auto;
        margin-top: 0;
        margin-left: 0.6rem;
      }
      @include media('>=medium') {
        order: 0;
        font-size: 0.8rem;
        height: 3rem;
        width: 18rem;
      }
    }
    &_dimensions {
      width: 100%;
      padding-bottom: 0.5rem;
      background: $brand-white;
      @include media('>=small') {
        width: 45%;
        flex-grow: 1;
        background: none;
        padding-right: 0.3rem;
        padding-bottom: 0;
      }
      @include media('>=medium-portrait') {
        padding-right: 0;
        width: auto;
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 11rem;
      }
      @include media('>=medium') {
        width: auto;
      }
    }
  }
}
</style>
